import { useCallback, useContext } from "react";
import analytics, { trackEvent } from "src/analytics";
import { AnalyticsEvent } from "src/analytics/events";
import fraction from "src/api/fraction";
import { ApplicationFlowStateContext } from "src/apps/AppFlow/screens/root";
import { Logger } from "src/log";
import { selectFullApplication } from "src/selectors";
import { clean } from "src/utilities/object";
import { getTrackingContext } from "src/utilities/tracking";

import { ValueError, parsers } from "@fraction/shared";

import usePartialSubmit, { PartialSubmitOptions } from "./usePartialSubmit";

type FullSubmitOptions = Pick<PartialSubmitOptions, "validate">;

const LOGGER = new Logger("pages.hooks.useFullSubmit");

/**
 * Submit the application in full. We do a partial submit first to check for any reason to reject the app.
 */
const useFullSubmit = ({ validate }: FullSubmitOptions = {}) => {
  const { state, dispatch } = useContext(ApplicationFlowStateContext);

  const onNext = useCallback(() => {
    LOGGER.log("Application flow completed");
    if (dispatch) {
      dispatch({ type: "change", prop: "isComplete", value: true });
    } else {
      throw new ValueError(`dispatch() is unexpectedly ${dispatch} in useFullSubmit()!`);
    }
    const app = parsers.application.FULL_APPLICATION.parse(
      clean(selectFullApplication(clean(state), { defaults: false }))
    );
    trackEvent(AnalyticsEvent.FULL_APP_SUBMITTED, app, getTrackingContext(app));
    if (app?.applicants?.[0]?.email) {
      analytics.identify(app?.applicants?.[0]?.email, app?.applicants?.[0], getTrackingContext(app));
    }
    fraction.submitFullApplication(app); // Send off the request to finish asynchronously
  }, [state, dispatch]);

  // We do a partial submission before the final full submission in order to check for rejection reasons. This
  // is in case a user went back in the app flow, changed something to be rejectable, and then scrolled back
  // down. Doing this would bypass the page-specific rejection checks.
  return usePartialSubmit({ onNext, validate, reasonsToBlock: "ANY" });
};

export default useFullSubmit;
